import { HttpMethod } from '@vision/ui/enums';
import {
  ApiResponse,
  JourneyMap,
  JourneyMapAttachmentUploadResponse,
  JourneyMapDashboardWidget,
  PlannerTask,
  type JourneyMapData,
  type JourneyMapPersonaData,
  type JourneyMapPersonaPayload,
  type JourneyMapResponse,
  type JourneyMapWidgetMutationPayload,
} from '@vision/ui/interfaces';
import { createHttpExtraOptions } from '@vision/ui/utils';
import { JourneyMapApi } from './journey-map.api';

const JourneyMapDetailEndpoints = JourneyMapApi.injectEndpoints({
  endpoints: (builder) => ({
    getJourneyMapDetail: builder.query<ApiResponse<JourneyMapResponse>, { accountId: string; journeyMapId: string }>({
      query: ({ accountId, journeyMapId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}`,
        method: HttpMethod.GET,
      }),
    }),
    getJourneyMapPlannerTasks: builder.query<ApiResponse<PlannerTask[]>, { accountId: string; journeyMapId: string }>({
      query: ({ accountId, journeyMapId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/tasks`,
        method: HttpMethod.GET,
      }),
    }),
    getJourneyMapWidgets: builder.query<
      ApiResponse<JourneyMapDashboardWidget[]>,
      { accountId: string; journeyMapId: string }
    >({
      query: ({ accountId, journeyMapId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/dashboard_widgets`,
        method: HttpMethod.GET,
      }),
    }),
    updateJourneyMap: builder.mutation<
      ApiResponse<JourneyMap>,
      { accountId: string; journeyMapId: string; data: JourneyMapData }
    >({
      query: ({ accountId, journeyMapId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.journeyMap.update.{{status}}',
          },
        }),
      }),
    }),
    updateJourneyMapSilently: builder.mutation<
      ApiResponse<JourneyMapResponse>,
      { accountId: string; journeyMapId: string; data: JourneyMapData }
    >({
      query: ({ accountId, journeyMapId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleError: false,
            autoHandleSuccess: false,
          },
        }),
      }),
    }),
    createJourneyMapWidget: builder.mutation<
      ApiResponse<JourneyMapDashboardWidget>,
      { accountId: string; journeyMapId: string; data: JourneyMapWidgetMutationPayload }
    >({
      query: ({ accountId, journeyMapId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/dashboard_widgets`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    createJourneyMapPersona: builder.mutation<
      ApiResponse<JourneyMapPersonaData>,
      { accountId: string; journeyMapId: string; data: JourneyMapPersonaPayload }
    >({
      query: ({ accountId, journeyMapId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/persona`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    updateJourneyMapPersona: builder.mutation<
      ApiResponse<JourneyMapPersonaData>,
      { accountId: string; journeyMapId: string; data: JourneyMapPersonaPayload }
    >({
      query: ({ accountId, journeyMapId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/persona/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    deleteJourneyMapPersona: builder.mutation<
      ApiResponse<{ success?: boolean }>,
      { accountId: string; journeyMapId: string }
    >({
      query: ({ accountId, journeyMapId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/persona/delete`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    getJourneyMapPersona: builder.query<
      ApiResponse<JourneyMapPersonaData>,
      { accountId: string; journeyMapId: string }
    >({
      query: ({ accountId, journeyMapId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/persona`,
        method: HttpMethod.GET,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleError: false,
            autoHandleSuccess: false,
          },
        }),
      }),
    }),
    updateJourneyMapWidget: builder.mutation<
      ApiResponse<JourneyMapDashboardWidget>,
      { accountId: string; journeyMapId: string; widgetId: string; data: JourneyMapWidgetMutationPayload }
    >({
      query: ({ accountId, journeyMapId, widgetId, data }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/dashboard_widgets/${widgetId}/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    deleteJourneyMapWidget: builder.mutation<
      ApiResponse<{ success?: boolean }>,
      { accountId: string; journeyMapId: string; widgetId: string }
    >({
      query: ({ accountId, journeyMapId, widgetId }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/dashboard_widgets/${widgetId}/delete`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            autoHandleError: false,
          },
        }),
      }),
    }),
    uploadJourneyMapAttachment: builder.mutation<
      ApiResponse<JourneyMapAttachmentUploadResponse>,
      { accountId: string; journeyMapId: string; tileId: string; formData: FormData }
    >({
      query: ({ accountId, journeyMapId, tileId, formData }) => ({
        url: `/v2/accounts/${accountId}/customer_journey_maps/${journeyMapId}/attachments?tile_id=${encodeURIComponent(tileId)}`,
        method: HttpMethod.POST,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.journeyMap.uploadAttachment.{{status}}',
          },
        }),
      }),
    }),
  }),
});

export const {
  useGetJourneyMapDetailQuery,
  useGetJourneyMapPlannerTasksQuery,
  useLazyGetJourneyMapWidgetsQuery,
  useLazyGetJourneyMapDetailQuery,
  useLazyGetJourneyMapPlannerTasksQuery,
  useUpdateJourneyMapMutation,
  useUpdateJourneyMapSilentlyMutation,
  useUpdateJourneyMapWidgetMutation,
  useCreateJourneyMapWidgetMutation,
  useCreateJourneyMapPersonaMutation,
  useUpdateJourneyMapPersonaMutation,
  useDeleteJourneyMapPersonaMutation,
  useGetJourneyMapPersonaQuery,
  useDeleteJourneyMapWidgetMutation,
  useUploadJourneyMapAttachmentMutation,
} = JourneyMapDetailEndpoints;
